import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Heart, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEvent } from '@/hooks/useEvent';
import { parseCalendarDate } from '@/lib/parseCalendarDate';

interface CoupleStepProps {
  brideName: string;
  groomName: string;
  weddingDate: string;
  onBrideNameChange: (value: string) => void;
  onGroomNameChange: (value: string) => void;
  onWeddingDateChange: (value: string) => void;
}

/** Formatea una fecha date-only sin corrimiento de zona horaria (US3 / FR-005). */
function formatWeddingDate(value: string): string {
  const parsed = parseCalendarDate(value);
  if (!parsed) return '';
  return parsed.toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const CoupleStep = ({
  brideName,
  groomName,
  weddingDate,
  onBrideNameChange,
  onGroomNameChange,
  onWeddingDateChange,
}: CoupleStepProps) => {
  // 006 — Precarga desde el Evento (fuente única). Solo siembra una vez, sin
  // pisar lo que el usuario ya empezó a escribir.
  const { data: event } = useEvent();
  const seededRef = useRef(false);

  useEffect(() => {
    if (seededRef.current || !event) return;
    seededRef.current = true;
    if (event.bride_name && !brideName) onBrideNameChange(event.bride_name);
    if (event.groom_name && !groomName) onGroomNameChange(event.groom_name);
    // wedding_date puede venir como TIMESTAMPTZ; tomamos la parte YYYY-MM-DD
    // sin `new Date()` para no correr el día (TZ-safe).
    if (event.wedding_date && !weddingDate) {
      onWeddingDateChange(event.wedding_date.slice(0, 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const formattedDate = formatWeddingDate(weddingDate);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-10"
      >
        <div className="flex items-center justify-center gap-2 mb-3">
          <Heart className="w-5 h-5 text-primary fill-primary" />
          <span className="text-sm tracking-widest uppercase text-muted-foreground">Los protagonistas</span>
          <Heart className="w-5 h-5 text-primary fill-primary" />
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-3">
          ¿Quiénes son los novios?
        </h2>
        <p className="text-muted-foreground">
          Estos nombres y la fecha aparecerán en tu invitación
        </p>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card/80 backdrop-blur-sm rounded-2xl border border-border/50 shadow-xl p-6 sm:p-8 space-y-8"
      >
        {/* Names preview */}
        <div className="text-center py-6 border-b border-border/50">
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Vista previa</p>
          <h3 className="font-serif text-3xl sm:text-4xl text-foreground">
            <span className={brideName ? 'text-foreground' : 'text-muted-foreground/50'}>
              {brideName || 'Novia'}
            </span>
            <span className="text-primary mx-3">&</span>
            <span className={groomName ? 'text-foreground' : 'text-muted-foreground/50'}>
              {groomName || 'Novio'}
            </span>
          </h3>
        </div>

        {/* Bride name */}
        <div className="space-y-2">
          <Label htmlFor="bride_name" className="text-base font-medium flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-blush-light flex items-center justify-center text-xs">👰</span>
            Nombre de la Novia
          </Label>
          <Input
            id="bride_name"
            value={brideName}
            onChange={(e) => onBrideNameChange(e.target.value)}
            placeholder="Ej: María"
            className="h-12 text-lg border-2 focus:border-primary transition-colors"
          />
        </div>

        {/* Groom name */}
        <div className="space-y-2">
          <Label htmlFor="groom_name" className="text-base font-medium flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-sage-light flex items-center justify-center text-xs">🤵</span>
            Nombre del Novio
          </Label>
          <Input
            id="groom_name"
            value={groomName}
            onChange={(e) => onGroomNameChange(e.target.value)}
            placeholder="Ej: Juan"
            className="h-12 text-lg border-2 focus:border-primary transition-colors"
          />
        </div>

        {/* Wedding date */}
        <div className="space-y-2">
          <Label htmlFor="wedding_date" className="text-base font-medium flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-gold-light flex items-center justify-center text-xs">📅</span>
            Fecha de la Boda
          </Label>
          <Input
            id="wedding_date"
            type="date"
            value={weddingDate}
            onChange={(e) => onWeddingDateChange(e.target.value)}
            className="h-12 text-lg border-2 focus:border-primary transition-colors"
          />
          {formattedDate && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="text-sm text-muted-foreground flex items-center gap-1.5 mt-2 capitalize"
            >
              <Sparkles className="w-4 h-4 text-gold" />
              {formattedDate}
            </motion.p>
          )}
        </div>
      </motion.div>

      {/* Note */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center text-sm text-muted-foreground mt-6"
      >
        ✨ Recuerda que puedes modificar estos datos cuando quieras
      </motion.p>
    </div>
  );
};

export default CoupleStep;
